import React from "react";
import type { HealthBand, SignalBand } from "../lib/types";
import { initials as toInitials, seedColor } from "../lib/format";

/* Shared visual primitives used across every view. */

const bandColor: Record<HealthBand, string> = {
  good: "var(--good)",
  watch: "var(--watch)",
  risk: "var(--risk)",
};

export function HealthBadge({ band, label }: { band: HealthBand; label?: string }) {
  const text = label ?? (band === "good" ? "Healthy" : band === "watch" ? "Watch" : "At risk");
  return <span className={`badge badge--${band}`}>{text}</span>;
}

export function SignalDot({ band }: { band: SignalBand }) {
  const cls = band === "risk" ? "i-risk" : band === "positive" ? "i-good" : "i-watch";
  return <i className={cls} />;
}

/** Concentric health ring. size in px. */
export function HealthRing({ value, size = 56, stroke = 5 }: { value: number; size?: number; stroke?: number }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const band: HealthBand = value >= 70 ? "good" : value >= 45 ? "watch" : "risk";
  const off = c * (1 - value / 100);
  return (
    <span className="ring-wrap" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--line)" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={bandColor[band]} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off}
          style={{ transition: "stroke-dashoffset .6s ease" }}
        />
      </svg>
      <b style={{ fontSize: size * 0.3, color: bandColor[band] }}>{value}</b>
    </span>
  );
}

/** Compact sparkline from a series of 0..100 values. */
export function Sparkline({
  data, width = 96, height = 28, band,
}: { data: number[]; width?: number; height?: number; band?: HealthBand }) {
  if (!data.length) return null;
  const min = Math.min(...data), max = Math.max(...data);
  const span = max - min || 1;
  const step = width / (data.length - 1);
  const pts = data.map((v, i) => [i * step, height - ((v - min) / span) * (height - 4) - 2]);
  const d = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const color = band ? bandColor[band] : "var(--corona)";
  const areaD = `${d} L${width} ${height} L0 ${height} Z`;
  const gid = React.useId();
  return (
    <svg width={width} height={height} aria-hidden="true">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaD} fill={`url(#${gid})`} />
      <path d={d} fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function Avatar({ name, color, size = "md" }: { name: string; color?: string; size?: "sm" | "md" | "lg" }) {
  const cls = size === "sm" ? "avatar avatar--sm" : size === "lg" ? "avatar avatar--lg" : "avatar";
  return (
    <span className={cls} style={{ background: color ?? seedColor(name) }}>
      {toInitials(name)}
    </span>
  );
}

export function Monogram({ name, size = "md" }: { name: string; size?: "md" | "lg" }) {
  return <span className={size === "lg" ? "mono mono--lg" : "mono"}>{name[0]}</span>;
}

export function Meter({ value, color }: { value: number; color?: string }) {
  return (
    <span className="meter" style={{ display: "block" }}>
      <i style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: color ?? "var(--corona)" }} />
    </span>
  );
}

export function Delta({ value, suffix = "" }: { value: number; suffix?: string }) {
  const pos = value >= 0;
  return (
    <span className={`stat__delta ${pos ? "pos" : "neg"}`}>
      {pos ? "▲" : "▼"} {Math.abs(value)}{suffix}
    </span>
  );
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <div className="empty">{children}</div>;
}
